<template>
  <!-- Therapy Room Section with Full-Width Image Gallery -->
  <section 
    id="therapy-room" 
    class="w-full relative overflow-hidden font-poppins bg-[#FAF9F7]"
    style="position: relative; z-index: 4;"
  >
    <!-- Full-width immersive hero image with overlay text -->
    <div class="relative w-full h-[85vh] overflow-hidden">
      <!-- Main image -->
      <img 
        :src="require(`../assets/image${currentImageIndex}.jpeg`)"
        alt="Therapy Room" 
        class="absolute inset-0 w-full h-full object-cover transform transition-transform duration-700"
        ref="mainImage"
        @click="openLightbox(currentImageIndex)"
      >
      
      <!-- Overlay gradient - updated to use brand colors -->
      <div class="absolute inset-0 bg-gradient-to-t from-[#333333]/80 via-[#333333]/40 to-transparent opacity-80"></div>
      
      <!-- Overlay content - simplified and more concise -->
      <div class="absolute bottom-0 left-0 right-0 p-8 md:p-16 lg:p-24">
        <div class="container mx-auto px-4 md:px-8">
          <div class="flex flex-col md:flex-row md:items-end md:justify-between">
            <div>
              <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">Therapy Room</h2>
              <div class="h-1 w-32 bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
              <p class="text-white/95 mt-4 text-l max-w-xl leading-relaxed">
                A comfortable, private space in Poole, Dorset where you can explore your thoughts and feelings without judgment.
              </p>
            </div>
            
            <!-- Navigation arrows for slider -->
            <div class="flex space-x-4 mt-8 md:mt-0">
              <button 
                @click.stop="prevImage" 
                class="w-12 h-12 rounded-full bg-[#333333]/30 text-white flex items-center justify-center hover:bg-[#333333]/50 transition-colors"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button 
                @click.stop="nextImage" 
                class="w-12 h-12 rounded-full bg-[#333333]/30 text-white flex items-center justify-center hover:bg-[#333333]/50 transition-colors"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Mobile only: Thumbnails below slider -->
    <div class="md:hidden py-6 bg-[#FAF9F7]">
      <div class="container mx-auto px-4">
        <div class="flex justify-center gap-4">
          <div 
            v-for="index in [0, 1]" 
            :key="index"
            @click="currentImageIndex = index"
            :class="[
              'relative w-20 h-20 rounded-md overflow-hidden cursor-pointer transition-all duration-300',
              currentImageIndex === index ? 'ring-2 ring-[#D2919A] shadow-lg scale-105' : 'ring-1 ring-[#D2919A]/20 opacity-70 hover:opacity-100'
            ]"
          >
            <img 
              :src="require(`../assets/image${index}.jpeg`)" 
              :alt="`Therapy Room - Thumbnail ${index + 1}`" 
              class="w-full h-full object-cover"
            >
          </div>
        </div>
      </div>
    </div>
    
    <!-- Lightbox gallery (hidden by default) -->
    <div 
      v-if="lightboxOpen" 
      class="fixed inset-0 z-50 bg-[#333333]/95 flex items-center justify-center p-4"
      @click="closeLightbox"
    >
      <div class="relative max-w-6xl max-h-[90vh] w-full" @click.stop>
        <!-- Image -->
        <img 
          :src="lightboxImages[currentLightboxIndex].src" 
          :alt="lightboxImages[currentLightboxIndex].alt" 
          class="w-full h-auto max-h-[80vh] object-contain"
        >
        
        <!-- Navigation -->
        <div class="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between px-4">
          <button 
            @click.stop="prevLightboxImage" 
            class="w-12 h-12 rounded-full bg-[#333333]/50 text-white flex items-center justify-center hover:bg-[#333333]/70 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            @click.stop="nextLightboxImage" 
            class="w-12 h-12 rounded-full bg-[#333333]/50 text-white flex items-center justify-center hover:bg-[#333333]/70 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <!-- Close button -->
        <button 
          @click.stop="closeLightbox"
          class="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#333333]/50 text-white flex items-center justify-center hover:bg-[#333333]/70 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <!-- Caption -->
        <div class="absolute bottom-0 left-0 right-0 p-4 bg-[#333333]/70 text-white">
          <p>{{ lightboxImages[currentLightboxIndex].caption }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from '@gsap/shockingly'
import { ScrollTrigger } from '@gsap/shockingly/ScrollTrigger'

export default {
  name: 'TherapyRoomSection',
  data() {
    return {
      animationsInitialized: false,
      lightboxOpen: false,
      currentLightboxIndex: 0,
      currentImageIndex: 0,
      lightboxImages: [
        {
          src: '../assets/image0.jpeg',
          alt: 'Therapy Room - View 1',
          caption: 'A calm environment for our sessions.'
        },
        {
          src: '../assets/image1.jpeg',
          alt: 'Therapy Room - View 2',
          caption: 'A private space where you can feel at ease.'
        }
      ]
    }
  },
  mounted() {
    try {
      // Register GSAP plugins safely
      if (typeof gsap !== 'undefined' && gsap.registerPlugin) {
        try {
          gsap.registerPlugin(ScrollTrigger);
        } catch (error) {
          console.warn('Error registering ScrollTrigger:', error);
        }
      }
      
      // Fix image paths
      this.lightboxImages.forEach((image, index) => {
        this.lightboxImages[index].src = require(`../assets/image${index}.jpeg`);
      });
      
      // Initialize animations once hero video is locked
      window.addEventListener('heroVideoLocked', this.initAnimations);
      
      // Backup initialization in case event is missed
      setTimeout(() => {
        if (!this.animationsInitialized) {
          this.initAnimations();
        }
      }, 3000);

      // Add keyboard event listeners for lightbox
      document.addEventListener('keydown', this.handleKeyPress);
      
      // Set up auto image rotation for the carousel
      this.startImageRotation();
    } catch (error) {
      console.error('Error in TherapyRoomSection mounted:', error);
    }
  },
  beforeUnmount() {
    window.removeEventListener('heroVideoLocked', this.initAnimations);
    document.removeEventListener('keydown', this.handleKeyPress);
    
    // Clear image rotation interval
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
    }
    
    // Clean up ScrollTrigger instances
    if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars.id && trigger.vars.id.startsWith('room-')) {
          trigger.kill();
        }
      });
    }
  },
  methods: {
    startImageRotation() {
      // Auto-rotate images every 5 seconds
      this.rotationInterval = setInterval(() => {
        this.nextImage();
      }, 5000);
    },
    
    nextImage() {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.lightboxImages.length;
    },
    
    prevImage() {
      this.currentImageIndex = (this.currentImageIndex - 1 + this.lightboxImages.length) % this.lightboxImages.length;
    },
    
    openLightbox(index) {
      this.currentLightboxIndex = index;
      this.lightboxOpen = true;
      
      // Pause auto rotation when lightbox is open
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
      }
      
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    },
    
    closeLightbox() {
      this.lightboxOpen = false;
      
      // Resume auto rotation
      this.startImageRotation();
      
      // Restore body scroll
      document.body.style.overflow = '';
    },
    
    nextLightboxImage() {
      this.currentLightboxIndex = (this.currentLightboxIndex + 1) % this.lightboxImages.length;
    },
    
    prevLightboxImage() {
      this.currentLightboxIndex = (this.currentLightboxIndex - 1 + this.lightboxImages.length) % this.lightboxImages.length;
    },
    
    handleKeyPress(e) {
      if (!this.lightboxOpen) return;
      
      if (e.key === 'Escape') {
        this.closeLightbox();
      } else if (e.key === 'ArrowRight') {
        this.nextLightboxImage();
      } else if (e.key === 'ArrowLeft') {
        this.prevLightboxImage();
      }
    },
    
    initAnimations() {
      if (this.animationsInitialized) return;
      this.animationsInitialized = true;
      
      console.log('Initializing TherapyRoomSection animations');
      
      // Only proceed if GSAP is available
      if (typeof gsap === 'undefined') return;
      
      // Create a master timeline for the section
      const masterTimeline = gsap.timeline({
        scrollTrigger: {
          id: 'room-master',
          trigger: '#therapy-room',
          start: 'top 80%',
          toggleActions: 'play none none none'
        }
      });
      
      // Animate in the main image with a more dramatic reveal
      if (this.$refs.mainImage) {
        masterTimeline.fromTo(this.$refs.mainImage, 
          {
            scale: 1.2,
            opacity: 0.5,
          }, 
          {
            scale: 1.0,
            opacity: 1,
            duration: 2.0,
            ease: 'power2.out'
          }, 0
        );
      }
    }
  }
}
</script>

<style scoped>
/* Updated background pattern with brand colors */
.bg-pattern-subtle {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D2919A' fill-opacity='0.3'%3E%3Cpath d='M36 34v6h6v-6h-6zm6 6v6h-6v-6h6zm-6-6v-6h6v6h-6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Enhanced scale transition */
.hover\:scale-102:hover {
  transform: scale(1.02);
}

/* Improved image transition effects */
.therapy-thumbnail img {
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.therapy-thumbnail:hover img {
  transform: scale(1.05);
}

/* Smooth transitions for all interactive elements */
button, a, .cursor-pointer {
  transition: all 0.3s ease;
}

/* Fix for Safari backdrop-filter */
@supports (backdrop-filter: blur(12px)) or (-webkit-backdrop-filter: blur(12px)) {
  .backdrop-blur-sm {
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
  }
}

/* Fix for mobile devices */
@media (max-width: 640px) {
  .h-\[85vh\] {
    height: 70vh;
  }
}

/* Add cursor style for images */
.cursor-zoom-in {
  cursor: zoom-in;
}
</style>